import classNames from 'classnames';
import styles from 'assets/jss/material-dashboard-pro-react/components/sidebarStyle';
import { makeStyles } from '@material-ui/core/styles';
import { NavLink } from 'react-router-dom';
import ListItemText from '@material-ui/core/ListItemText/ListItemText';
import Collapse from '@material-ui/core/Collapse';
import ListItem from '@material-ui/core/ListItem';
import React, { useEffect, useMemo, useState } from 'react';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import Links from './Links';

// @ts-ignore
const useStyles = makeStyles(styles);

interface ILinkProps {
	miniEnabled: boolean;
	miniActive: boolean;
	rtlActive: boolean;
	route: types.routes.ISingleDashboardRoute;
	color: types.sidebar.color;
	pathname: string;
	onLinkClick: () => void;
}

const Link = ({ miniActive, miniEnabled, rtlActive, route, color, pathname, onLinkClick }: ILinkProps) => {
	const collapseActive: boolean = useMemo(() => {
		if (route.collapse && route.views && route.views.length > 0) {
			return route.views.some(
				(singleRoute) => pathname.indexOf(`${singleRoute.layout}${singleRoute.path}`) !== -1
			);
		}
		return false;
	}, [pathname, route]);
	const [isOpen, setIsOpen] = useState<boolean>(collapseActive);
	const classes = useStyles();
	const routePath: string = route.layout + route.path;
	useEffect(() => {
		/** if subrouting is true as per upper written logic, making dropdown open */
		if (collapseActive) setIsOpen(true);
	}, [collapseActive]);
	const linkClicked = (): void => {
		if (route.collapse) {
			setIsOpen((open) => !open);
		} else {
			onLinkClick();
		}
	};

	const activeRoute: boolean = useMemo(() => {
		return pathname.indexOf(routePath) > -1;
	}, [routePath, pathname]);

	const icon = useMemo(() => {
		if (route.icon) {
			return <route.icon />;
		} else {
			return null;
		}
	}, [route]);

	const itemIsMultiline = route.name.length > 25;

	const baseNavLinkClasses = classNames(classes.itemLink, {
		[classes.collapseActive]: collapseActive,
		[classes.miniActive]: miniActive && miniEnabled,
		[classes[color]]: activeRoute && !route.collapse,
		[classes.activeLink]: activeRoute && !route.collapse,
	});
	const itemText = classNames(classes.itemText, {
		[classes.itemTextMultiline]: itemIsMultiline,
		[classes.itemTextMini]: miniActive && miniEnabled,
		[classes.itemTextMiniRTL]: rtlActive && miniActive && miniEnabled,
		[classes.itemTextRTL]: rtlActive,
		[classes.miniActive]: miniActive && miniEnabled && route.collapse,
	});
	const collapseItemText = classNames(classes.collapseItemText, {
		[classes.collapseItemTextMini]: miniActive && miniEnabled,
		[classes.collapseItemTextMiniRTL]: rtlActive && miniActive && miniEnabled,
		[classes.collapseItemTextRTL]: rtlActive,
	});
	const itemIcon = classNames(classes.itemIcon, {
		[classes.itemIconMultiline]: itemIsMultiline,
		[classes.itemIconRTL]: rtlActive,
	});
	const caret = classNames(classes.caret, {
		[classes.caretRTL]: rtlActive,
	});
	const innerNavLinkClasses = classNames(classes.collapseItemLink, {
		[classes[color]]: activeRoute,
		[classes.activeLink]: activeRoute,
	});
	const listItem = classNames({ [classes.item]: !!route.icon, [classes.collapseItem]: !route.icon });
	const listItemText = classNames({ [itemText]: !!route.icon, [collapseItemText]: route.collapse && !route.icon });
	const navLinkClasses = classNames({
		[baseNavLinkClasses]: route.collapse || !!route.icon,
		[innerNavLinkClasses]: !route.collapse && !route.icon,
		[classes.collapseItemWithIcon]: !route.collapse && route.collapseItem,
	});
	const caretClasses = classNames(caret, { [classes.caretActive]: isOpen });

	if (route.customPath) {
		return (
			<ListItem className={listItem}>
				<a href={route.customPath} className={navLinkClasses} onClick={linkClicked}>
					<ConditionalRender show={!!icon} onTrue={() => <div className={itemIcon}>{icon}</div>} />
					<ListItemText
						primary={route.name}
						secondary={route.collapse && <b className={caretClasses} />}
						disableTypography={true}
						className={listItemText}
					/>
				</a>
			</ListItem>
		);
	}

	const onClicked = (event: React.MouseEvent) => {
		if (route.collapse) {
			event.preventDefault();
		}

		if (location.pathname === routePath) {
			location.href = routePath;
		}
		linkClicked();
	};
	return (
		<ListItem className={listItem}>
			<NavLink to={route.collapse ? '#' : routePath} className={navLinkClasses} onClick={onClicked}>
				<ConditionalRender show={!!icon} onTrue={() => <div className={itemIcon}>{icon}</div>} />
				<ListItemText
					primary={route.name}
					secondary={route.collapse && <b className={caretClasses} />}
					disableTypography={true}
					className={listItemText}
				/>
			</NavLink>
			<ConditionalRender
				show={!!route.collapse && !!route.views && route.views.length > 0}
				onTrue={() => (
					<Collapse in={isOpen} timeout="auto" unmountOnExit>
						<Links
							color={color}
							rtlActive={rtlActive}
							routes={route.views!}
							miniEnabled={miniEnabled}
							className={classes.collapseList}
							miniActive={miniActive}
							pathname={pathname}
							onLinkClick={onLinkClick}
						/>
					</Collapse>
				)}
			/>
		</ListItem>
	);
};

export default Link;
